/**
 * MinistrareIA - Auth Context
 * Authentication state management with onboarding support
 * 
 * UPDATED: 
 * - Removed infinitagentsApiKey from register (now automatic)
 * - Added updateUser function and onboarding-aware redirects
 * FIX: Added token to context value (was missing, breaking SSE connection)
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/global/authService';
import onboardingService from '../services/global/onboardingService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // ✅ ADD: Token state
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = authService.getStoredUser();
        const storedToken = authService.getToken(); // ✅ Get token

        if (storedUser && storedToken) {
          // Verify token is still valid by fetching profile
          try {
            const response = await authService.getProfile();
            const freshUser = response.data;
            
            // Update stored user with fresh data
            authService.storeAuthData(freshUser, storedToken);
            setUser(freshUser);
            setToken(storedToken); // ✅ SET token
            setIsAuthenticated(true);
          } catch (error) {
            // Token invalid, clear storage
            console.warn('Token expired or invalid, logging out');
            authService.logout();
            setUser(null);
            setToken(null); // ✅ CLEAR token
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Register new user
   * 
   * InfinitAgents API key is now generated automatically by the backend.
   * If integration fails, use authService.retryInfinitAgentsIntegration()
   * 
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} name - User name
   */
  const register = useCallback(async (email, password, name) => {
    // 🆕 UPDATED: No longer passing infinitagentsApiKey (now automatic)
    const response = await authService.register(email, password, name);
    const { user: newUser, token: newToken } = response.data;
    
    authService.storeAuthData(newUser, newToken);
    setUser(newUser);
    setToken(newToken); // ✅ SET token
    setIsAuthenticated(true);
    
    return newUser;
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (email, password) => {
    const response = await authService.login(email, password);
    const { user: loggedInUser, token: newToken } = response.data;
    
    authService.storeAuthData(loggedInUser, newToken);
    setUser(loggedInUser);
    setToken(newToken); // ✅ SET token
    setIsAuthenticated(true);
    
    return loggedInUser;
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setToken(null); // ✅ CLEAR token
    setIsAuthenticated(false);
  }, []);

  /**
   * Update user in context (without re-fetching)
   * Used after onboarding, channel selection, etc.
   */
  const updateUser = useCallback((updatedUser) => {
    const currentToken = authService.getToken();
    if (currentToken) {
      authService.storeAuthData(updatedUser, currentToken);
    }
    setUser(updatedUser);
  }, []);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getProfile();
      const freshUser = response.data;
      const currentToken = authService.getToken();
      
      if (currentToken) {
        authService.storeAuthData(freshUser, currentToken);
      }
      setUser(freshUser);
      
      return freshUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  }, []);

  /**
   * Get redirect path based on user state
   */
  const getRedirectPath = useCallback(() => {
    return onboardingService.getRedirectPath(user);
  }, [user]);

  /**
   * Check if user needs onboarding
   */
  const needsOnboarding = useCallback(() => {
    return onboardingService.needsOnboarding(user);
  }, [user]);

  const value = {
    user,
    token, // ✅ ADD: Expose token to consumers (SSEContext needs this!)
    loading,
    isAuthenticated,
    register,
    login,
    logout,
    updateUser,
    refreshUser,
    getRedirectPath,
    needsOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;